// ─────────────────────────────────────────────────────────────────────────────
// Combobox de personas contra el directorio (GET v1/users/search), con foto real y caída a
// iniciales. Antes vivía como componente privado de `ReconocerPanel.tsx`; se saca aquí porque la
// tira de Reportería necesita exactamente el mismo buscador y tener dos habría garantizado que
// divergieran — ya pasó con `UserSearchSelect`, que solo pinta iniciales.
//
// Añade sobre el original el estado SELECCIONADO: la tira necesita saber a QUIÉN se eligió (el id
// viaja al backend), así que el control tiene que poder mostrar la elección y permitir deshacerla.
// ─────────────────────────────────────────────────────────────────────────────
import {
  useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { usePeopleSearch, useAvatarUrlResolver } from '@/api/recognition';
import { useDropdownPosition } from '@/components/common/OuTree/useDropdownPosition';
import { PersonAvatar } from '@/views/recognition/postitShared';
import './PersonPicker.css';

export interface PersonaElegida {
  id: string;
  displayName: string;
  /** Anulable: un frontline puede no tener correo. */
  email: string | null;
  photoUrl?: string | null;
  avatar?: string | null;
}

interface PersonPickerProps {
  readonly value: PersonaElegida | null;
  readonly onChange: (persona: PersonaElegida | null) => void;
  readonly placeholder: string;
  readonly excludeIds?: readonly string[];
  readonly inputId?: string;
  /**
   * Punto 12 (revisión final, 2026-09-03) — el prop `baseClass` se retira: ningún consumidor
   * (`ReconocerPanel`, la tira de Reportería) lo pasaba nunca, así que las clases del componente
   * siempre fueron `pp-*` en la práctica. Sostenerlo como configurable era además la causa raíz del
   * punto 11: con el prefijo fijo por omisión, dos `PersonPicker` montados a la vez (destinatarios +
   * delegado, en `ReconocerPanel`) generaban el MISMO `id="pp-listbox"` en el DOM.
   */
  /**
   * Punto 12 — variante visual del buscador cuando delega en un supervisor («a nombre de»,
   * `ReconocerPanel`): borde naranja, recuperada de `.rp-search-row--supervisor` (retirada al migrar
   * ese buscador a este componente compartido — quedó huérfana en `recognition.css`).
   */
  readonly isSupervisorVariant?: boolean;
  /**
   * Cuántas personas ofrece el desplegable. Por omisión seis, que es lo que cabe sin tapar la
   * pantalla y lo que usan los filtros de Reportería.
   *
   * Los dos buscadores de `ReconocerPanel` —destinatarios y «a nombre de»— piden 100
   * (QA 2026-09-15: con seis, encontrar a alguien en un directorio de decenas de miles era
   * cuestión de suerte). Es una prop y no una constante nueva a propósito: la tira de Reportería
   * filtra sobre un universo ya acotado y su desplegable convive con el resto de controles, así
   * que subírselo también habría empeorado esa pantalla sin que nadie lo pidiera.
   */
  readonly maxOptions?: number;
  /**
   * Función 1 «A nombre de»: `'delegate'` en la caja de emisor de `ReconocerPanel`, para que el servidor
   * aplique la regla de emisor elegible. Sin él (receptores, Reportería), la búsqueda de siempre.
   */
  readonly searchPurpose?: 'delegate';
}

/** Mínimo de letras antes de consultar. El backend devuelve vacío por debajo de 2; no se le pega. */
const MINIMO_LETRAS = 2;
const DEBOUNCE_MS = 250;
/** Tope de opciones por omisión. Ver `maxOptions` para por qué es configurable. */
const MAXIMO_OPCIONES = 6;

/**
 * Punto 3 (revisión final, 2026-09-03) — BLOQUEANTE (visual): el desplegable vive en un portal
 * (`document.body`), así que un selector de ANCESTRO (`.shine-home .pp-dropdown`) nunca puede
 * casarlo — no es descendiente del hero oscuro en el DOM, aunque lo sea visualmente. Este hook
 * calcula el contexto de tema del propio componente (no de dónde cuelga el portal) para que el
 * modificador `.pp--dark` se pueda aplicar DIRECTAMENTE al `<ul>` portado, además de a la raíz.
 *
 * Dos fuentes, cualquiera activa el tema oscuro:
 *  - `.shine-home` como ancestro real de la raíz del control (Brillamos Juntos, `ReconocerPanel`).
 *  - `body.dark`, el tema oscuro real del repo (`ThemeContext.tsx`) — vía `MutationObserver` en vez
 *    de `useThemeContext()` para no exigirle `ThemeProvider` a cada test que monta este control.
 */
function useEsTemaOscuro(raiz: RefObject<HTMLElement>): boolean {
  const [esOscuro, setEsOscuro] = useState(false);

  useEffect(() => {
    function recalcular() {
      const enShineHome = !!raiz.current?.closest('.shine-home');
      const enBodyOscuro = document.body.classList.contains('dark');
      setEsOscuro(enShineHome || enBodyOscuro);
    }
    recalcular();
    const observador = new MutationObserver(recalcular);
    observador.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observador.disconnect();
  }, [raiz]);

  return esOscuro;
}

export function PersonPicker({
  value, onChange, placeholder, excludeIds = [], inputId, isSupervisorVariant = false,
  maxOptions = MAXIMO_OPCIONES, searchPurpose,
}: PersonPickerProps) {
  const { t } = useTranslation('recognition');
  const urlDeAvatar = useAvatarUrlResolver();
  const [texto, setTexto] = useState('');
  const [termino, setTermino] = useState('');
  const [estaAbierto, setEstaAbierto] = useState(false);
  const [indiceActivo, setIndiceActivo] = useState(-1);
  const raizRef = useRef<HTMLDivElement>(null);
  /** El `<ul>` portado. Necesario para el detector de clic-fuera: ver `alClicarFuera`. */
  const desplegableRef = useRef<HTMLUListElement>(null);
  const esOscuro = useEsTemaOscuro(raizRef);
  // Punto 11 — instancia real, no el prefijo compartido: sin esto, dos pickers montados a la vez
  // (destinatarios + delegado en `ReconocerPanel`) repetían `id="pp-listbox"`.
  const idInstancia = useId();
  const idListbox = `pp-listbox-${idInstancia}`;

  useEffect(() => {
    const temporizador = setTimeout(() => setTermino(texto), DEBOUNCE_MS);
    return () => clearTimeout(temporizador);
  }, [texto]);

  useEffect(() => {
    function alClicarFuera(evento: MouseEvent) {
      const objetivo = evento.target as Node;
      // 🔴 QA 2026-09-15 — el desplegable vive en un PORTAL colgado de `document.body`, así que no
      // es descendiente de `raizRef`. Comprobar solo la raíz hacía que pulsar la BARRA DE
      // DESPLAZAMIENTO del propio desplegable contara como "clic fuera" y lo cerrara: la lista
      // desaparecía justo al intentar arrastrarla, y solo se podía recorrer con la rueda.
      // Las opciones no sufrían el defecto porque se eligen con `onMouseDown` y ganan la carrera
      // (ver el comentario de `<li>` más abajo); la barra no tiene handler propio que la gane.
      const dentroDeLaRaiz = raizRef.current?.contains(objetivo) ?? false;
      const dentroDelDesplegable = desplegableRef.current?.contains(objetivo) ?? false;
      if (!dentroDeLaRaiz && !dentroDelDesplegable) setEstaAbierto(false);
    }
    document.addEventListener('mousedown', alClicarFuera);
    return () => document.removeEventListener('mousedown', alClicarFuera);
  }, []);

  const resultados = usePeopleSearch({
    search: termino,
    pageSize: maxOptions,
    // Solo si viene: un `purpose: undefined` en la clave no cambia nada, pero se deja fuera para que la
    // query key de los demás buscadores sea exactamente la de siempre.
    ...(searchPurpose ? { purpose: searchPurpose } : {}),
  });
  const opciones = (resultados.data?.items ?? [])
    .filter((persona) => !excludeIds.includes(persona.id))
    .slice(0, maxOptions);

  const hayTerminoValido = termino.trim().length >= MINIMO_LETRAS;
  const mostrarDesplegable = estaAbierto && !value && hayTerminoValido;
  const posicion = useDropdownPosition(raizRef, mostrarDesplegable);

  function elegir(persona: (typeof opciones)[number]) {
    onChange({
      id: persona.id,
      displayName: persona.displayName ?? persona.email ?? persona.id,
      email: persona.email,
      photoUrl: persona.photoUrl,
      avatar: persona.avatar,
    });
    setTexto('');
    setEstaAbierto(false);
  }

  function alTeclear(evento: KeyboardEvent<HTMLInputElement>) {
    if (evento.key === 'Escape') { setEstaAbierto(false); return; }
    if (!mostrarDesplegable || opciones.length === 0) return;
    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      setIndiceActivo((previo) => (previo + 1) % opciones.length);
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      setIndiceActivo((previo) => (previo - 1 + opciones.length) % opciones.length);
    } else if (evento.key === 'Enter' && indiceActivo >= 0) {
      evento.preventDefault();
      elegir(opciones[indiceActivo]);
    }
  }

  const estiloDesplegable = posicion
    ? ({
        '--pp-top': `${posicion.top}px`,
        '--pp-left': `${posicion.left}px`,
        '--pp-width': `${posicion.width}px`,
        '--pp-max-height': `${posicion.maxHeight}px`,
      } as CSSProperties)
    : undefined;

  /** Id de la opción activa — base de `aria-activedescendant` (punto 11: sin esto, navegar con
   *  flechas no anuncia nada a un lector de pantalla). */
  const idOpcion = (personaId: string) => `${idListbox}-opt-${personaId}`;
  const opcionActiva = indiceActivo >= 0 ? opciones[indiceActivo] : undefined;

  if (value) {
    // 2026-10-06 — la raíz del chip también lleva `.pp--dark` (antes solo la del buscador vacío): sin
    // ella, en tema oscuro el chip caía al fondo blanco de respaldo y el nombre, que heredaba el texto
    // claro del tema, no se leía (Reportería › Líderes y Moderación).
    return (
      <div className={`pp${esOscuro ? ' pp--dark' : ''}`} ref={raizRef}>
        <span className="pp-chip">
          <PersonAvatar
            name={value.displayName}
            photoUrl={value.photoUrl ?? urlDeAvatar(value.avatar)}
            baseClass="pp-chip-av"
            seed={value.id}
          />
          <span className="pp-chip-name">{value.displayName}</span>
          <button
            type="button"
            className="pp-chip-remove"
            onClick={() => onChange(null)}
            aria-label={t('reports.filters.personClear')}
          >
            <i className="fas fa-xmark" aria-hidden="true" />
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className={`pp${isSupervisorVariant ? ' pp--supervisor' : ''}${esOscuro ? ' pp--dark' : ''}`} ref={raizRef}>
      <i className="fas fa-search pp-icon" aria-hidden="true" />
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={mostrarDesplegable}
        aria-controls={idListbox}
        aria-autocomplete="list"
        aria-activedescendant={opcionActiva ? idOpcion(opcionActiva.id) : undefined}
        className="pp-input"
        placeholder={placeholder}
        aria-label={placeholder}
        value={texto}
        onChange={(evento) => { setTexto(evento.target.value); setEstaAbierto(true); setIndiceActivo(-1); }}
        onFocus={() => setEstaAbierto(true)}
        onKeyDown={alTeclear}
      />
      {mostrarDesplegable && posicion && createPortal(
        <ul
          ref={desplegableRef}
          className={`pp-dropdown${esOscuro ? ' pp--dark' : ''}`}
          id={idListbox}
          role="listbox"
          style={estiloDesplegable}
        >
          {resultados.isLoading && (
            <li className="pp-status">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" /> {t('reports.filters.personSearching')}
            </li>
          )}
          {resultados.isError && !resultados.isLoading && (
            <li className="pp-status pp-status--error">{t('reports.filters.personError')}</li>
          )}
          {!resultados.isLoading && !resultados.isError && opciones.length === 0 && (
            <li className="pp-status">{t('reports.filters.personNoResults')}</li>
          )}
          {!resultados.isError && opciones.map((persona, indice) => (
            <li
              key={persona.id}
              id={idOpcion(persona.id)}
              role="option"
              aria-selected={indice === indiceActivo}
              className={`pp-option${indice === indiceActivo ? ' active' : ''}`}
              // `onMouseDown`, NO `onClick`: el desplegable vive en un portal (`document.body`),
              // fuera del subárbol de `raizRef`. El listener de clic-fuera escucha `mousedown` en
              // `document` para cerrar el desplegable; si esta opción usara `onClick`, el cierre por
              // "clic fuera" ya habría desmontado el `<li>` para cuando el navegador despacha el
              // `click` (evento posterior), y la selección nunca llegaría a ocurrir. Con
              // `onMouseDown`, React entrega el evento del portal (delegado en `document.body`) ANTES
              // de que el listener en `document` lo vea, así que la selección gana la carrera.
              onMouseDown={() => elegir(persona)}
            >
              <PersonAvatar
                name={persona.displayName ?? persona.email ?? ''}
                photoUrl={persona.photoUrl ?? urlDeAvatar(persona.avatar)}
                baseClass="pp-option-av"
                seed={persona.id}
              />
              <span className="pp-option-text">
                <span className="pp-option-name">{persona.displayName ?? persona.email}</span>
                {persona.jobTitle && <span className="pp-option-sub">{persona.jobTitle}</span>}
              </span>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
